/**
 * NotesTaker component - the main notes UI with Server-Sent Events.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import type { JSONContent } from "@tiptap/react";
import { NotesEditor } from "./NotesEditor";
import { ContactSummary } from "./ContactSummary";
import { tiptapToTree } from "../../lib/treeConverter";
import { useNotesStream } from "../../hooks/useNotesStream";
import type { TreeNode, NotesState } from "../../lib/types";
import { openNotesPopout } from "../../lib/notesPopout";
import { Customer360Panel } from "../customer360/Customer360Panel";
import { Customer360Timeline } from "../customer360/Customer360Timeline";
import { Customer360Tabbed } from "../customer360/Customer360Tabbed";
import type { CustomerProfile } from "../customer360/Customer360.types";
import { useCustomer360Live } from "../../hooks/useCustomer360Live";
import { useAppContext } from "../../contexts/AppContext";
import type { C360StepEvent } from "../../lib/api";
import { AttachmentsPanel } from "../attachments";

export interface C360LiveState {
  status: string;
  steps: C360StepEvent[];
  result: unknown | null;
  error: Record<string, unknown> | null;
}

// =============================================================================
// Layout persistence (ported from calltree-tools-ui c360-search.tsx)
// =============================================================================

type C360Layout = "panel" | "timeline" | "tabbed";
const DEFAULT_LAYOUT: C360Layout = "panel";
const LAYOUT_STORAGE_KEY = "c360:layout";
/** Stored preferences older than this cutoff are ignored and the default
 *  is used instead. Bump this when the default layout changes so existing
 *  users pick up the new default without manually switching. */
const LAYOUT_RESET_CUTOFF_MS = Date.UTC(2026, 3, 20, 20, 0, 0); // 2026-04-20 20:00 UTC

interface StoredLayout {
  layout: C360Layout;
  ts: number;
}

function isC360Layout(v: unknown): v is C360Layout {
  return v === "panel" || v === "timeline" || v === "tabbed";
}

function readSavedLayout(): C360Layout {
  if (typeof window === "undefined") return DEFAULT_LAYOUT;
  try {
    const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return DEFAULT_LAYOUT;
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed === "object" && parsed !== null && "layout" in parsed && "ts" in parsed) {
        const p = parsed as { layout?: unknown; ts?: unknown };
        if (typeof p.ts === "number" && p.ts >= LAYOUT_RESET_CUTOFF_MS && isC360Layout(p.layout)) {
          return p.layout;
        }
      }
    } catch { /* not JSON — fall through */ }
    return DEFAULT_LAYOUT;
  } catch {
    return DEFAULT_LAYOUT;
  }
}

function saveLayout(layout: C360Layout): void {
  if (typeof window === "undefined") return;
  try {
    const payload: StoredLayout = { layout, ts: Date.now() };
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(payload));
  } catch { /* storage unavailable */ }
}

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    background: "#ffffff",
  },
  header: {
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
  },
  statusBadge: {
    fontSize: "11px",
    fontWeight: 500,
    padding: "4px 10px",
    borderRadius: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statusLive: {
    background: "#dcfce7",
    color: "#166534",
  },
  statusCompleted: {
    background: "#e5e7eb",
    color: "#374151",
  },
  statusConnecting: {
    background: "#fef3c7",
    color: "#92400e",
  },
  tabs: {
    display: "flex",
    gap: "4px",
    marginLeft: "16px",
  },
  tab: {
    padding: "6px 14px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#6b7280",
    background: "transparent",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "background 0.15s, color 0.15s",
  },
  tabActive: {
    background: "#3b82f6",
    color: "#ffffff",
  },
  tabDisabled: {
    color: "#d1d5db",
    cursor: "not-allowed",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  mainArea: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  editorArea: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  emptyState: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
  },
  popoutButton: {
    border: "1px solid #d1d5db",
    background: "#ffffff",
    color: "#374151",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: 600,
    padding: "6px 10px",
    cursor: "pointer",
  },
};

// =============================================================================
// Types
// =============================================================================

const C360_STEP_LABELS: Record<string, string> = {
  _search_customer_profiles: "Searching profiles",
  _expand_profiles: "Expanding profiles",
  _get_account_id: "Resolving account",
  _fetch_profile_short: "Fetching profile data",
  _fetch_profile_full: "Fetching profile data",
  _format_result: "Formatting results",
  _publish_workflow_completion: "Publishing",
};

interface NotesTakerProps {
  streamingEnabled?: boolean;
  contactId: string;
  showHeader?: boolean;
  renderDebug?: (notes: NotesState | null) => React.ReactNode;
  onPopOut?: (contactId: string) => void;
  showPopoutButton?: boolean;
  /** C360 query — triggers the durable Lambda progressive lookup. */
  c360Query?: string;
  /** Called whenever the C360 live state changes (status, result, etc.).
   *  Used by AgentApp to forward state to the popout window. */
  onC360StateChange?: (state: C360LiveState) => void;
  /** External C360 state received from the parent window. When provided,
   *  the hook skips auto-invoke and uses this data instead. */
  externalC360?: C360LiveState | null;
  /** Delegate a C360 search to the parent window (popout → parent). */
  onC360Search?: (query: string) => void;
  /** Ref populated with a function to trigger a C360 search from outside. */
  c360SearchRef?: React.MutableRefObject<((query: string) => void) | null>;
}

// =============================================================================
// Component
// =============================================================================

export function NotesTaker({
  streamingEnabled = true,
  contactId,
  showHeader = true,
  renderDebug,
  onPopOut,
  showPopoutButton = true,
  c360Query,
  onC360StateChange,
  externalC360,
  onC360Search,
  c360SearchRef,
}: NotesTakerProps) {
  const { notes, setNotes, connectionStatus, syncTree } = useNotesStream(contactId, streamingEnabled);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeTab, setActiveTab] = useState<
    "notes" | "summary" | "customer360"
  >("notes");
  const [c360Variant, setC360Variant] = useState<C360Layout>(readSavedLayout);
  const handleSetC360Variant = useCallback((v: C360Layout) => {
    setC360Variant(v);
    saveLayout(v);
  }, []);

  const { contactChannel, isFeatureEnabled } = useAppContext();
  const realtimeNotesEnabled = isFeatureEnabled("realtime_notes");
  const visibleTab = activeTab === "summary" && !realtimeNotesEnabled ? "notes" : activeTab;
  const attachmentsEnabled = isFeatureEnabled("attachments");
  // Live C360 lookup via durable Lambda (when c360Query is set).
  // Skip auto-invoke when:
  //  - the parent forwarded external state (popout case), OR
  //  - the customer_360 feature is not enabled for this agent
  //    (prevents non-allowlisted agents from triggering Lambda calls
  //     via the hidden tab, and from leaking C360 data to the popout).
  const c360FeatureEnabled = isFeatureEnabled("customer_360");
  const c360Hook = useCustomer360Live(
    contactId, c360Query ?? "", "full", contactChannel, !externalC360 && c360FeatureEnabled,
  );
  const c360Live = externalC360 ?? c360Hook;

  // Expose invoke to parent via ref (for popout → parent search delegation).
  useEffect(() => {
    if (c360SearchRef) c360SearchRef.current = c360Hook.invoke;
    return () => { if (c360SearchRef) c360SearchRef.current = null; };
  }, [c360SearchRef, c360Hook.invoke]);

  // Forward state changes to the parent (for popout sync).
  const onC360StateChangeRef = useRef(onC360StateChange);
  onC360StateChangeRef.current = onC360StateChange;
  useEffect(() => {
    onC360StateChangeRef.current?.({
      status: c360Hook.status,
      steps: c360Hook.steps,
      result: c360Hook.result,
      error: c360Hook.error,
    });
  }, [c360Hook.status, c360Hook.steps, c360Hook.result, c360Hook.error]);

  const c360Profile: CustomerProfile | null = (() => {
    const r = c360Live.result as Record<string, unknown> | null;
    if (!r) return null;
    const raw = r.c360_profiles_json;
    if (typeof raw === "string") {
      try { return (JSON.parse(raw) as CustomerProfile[])[0] ?? null; } catch { return null; }
    }
    if (Array.isArray(raw)) return (raw as CustomerProfile[])[0] ?? null;
    if (Array.isArray(r.profiles)) return (r.profiles as CustomerProfile[])[0] ?? null;
    return null;
  })();
  const c360Available = (!!c360Query || !!externalC360) && c360FeatureEnabled;

  // Check if contact is completed (summary tab available)
  const isCompleted = notes?.status === "completed" || connectionStatus === "completed";

  // Debounced sync to backend
  const handleContentChange = useCallback(
    (tiptapDoc: JSONContent) => {
      // Convert TipTap doc to tree, reusing existing IDs where possible
      const treeRoot = tiptapToTree(tiptapDoc);

      // Update local state immediately
      setNotes((prev) => (prev ? { ...prev, tree: treeRoot } : prev));

      // Clear previous timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      // Debounce: wait 1 second after typing stops to sync to backend
      syncTimeoutRef.current = setTimeout(() => {
        if (contactId) {
          syncTree(contactId, treeRoot);
        }
      }, 1000);
    },
    [contactId, syncTree, setNotes]
  );

  // Handle lock toggle - update local state and sync immediately
  const handleToggleLock = useCallback(
    (nodeId: string, locked: boolean) => {
      console.log(`Toggle lock: ${nodeId} -> ${locked}`);

      // Update the node's locked attr in local tree state
      setNotes((prev) => {
        if (!prev?.tree) return prev;

        // Deep clone and update the locked attr
        const updateLocked = (node: TreeNode): TreeNode => {
          if (node.id === nodeId) {
            return { ...node, attrs: { ...node.attrs, locked } };
          }
          if (node.children) {
            return { ...node, children: node.children.map(updateLocked) };
          }
          return node;
        };

        const newTree = updateLocked(prev.tree);

        // Sync immediately (no debounce for lock changes)
        if (contactId) {
          syncTree(contactId, newTree);
        }

        return { ...prev, tree: newTree };
      });
    },
    [contactId, syncTree, setNotes]
  );

  const getStatusStyle = (): React.CSSProperties => {
    switch (connectionStatus) {
      case "live":
        return { ...styles.statusBadge, ...styles.statusLive };
      case "completed":
        return { ...styles.statusBadge, ...styles.statusCompleted };
      default:
        return { ...styles.statusBadge, ...styles.statusConnecting };
    }
  };

  const getStatusText = (): string => {
    switch (connectionStatus) {
      case "live":
        return "Live";
      case "completed":
        return "Completed";
      case "connecting":
        return "Connecting...";
      case "error":
        return "Error";
      default:
        return "Disconnected";
    }
  };

  const handlePopOut = useCallback(() => {
    const popout = onPopOut ?? openNotesPopout;
    popout(contactId);
  }, [contactId, onPopOut]);

  return (
    <div style={styles.container}>
      {showHeader && (
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.headerTitle}>Notepad</h1>
            <div style={styles.tabs}>
              <button
                style={{
                  ...styles.tab,
                  ...(visibleTab === "notes" ? styles.tabActive : {}),
                }}
                onClick={() => setActiveTab("notes")}
              >
                Notes
              </button>
              {realtimeNotesEnabled && (
              <button
                style={{
                  ...styles.tab,
                  ...(visibleTab === "summary" ? styles.tabActive : {}),
                  ...(!isCompleted ? styles.tabDisabled : {}),
                }}
                onClick={() => isCompleted && setActiveTab("summary")}
                disabled={!isCompleted}
                title={isCompleted ? "View contact summary" : "Available when contact is completed"}
              >
                Summary
              </button>
              )}
              {c360Available && (
                <button
                  style={{
                    ...styles.tab,
                    ...(visibleTab === "customer360" ? styles.tabActive : {}),
                  }}
                  onClick={() => setActiveTab("customer360")}
                >
                  Customer 360
                  {c360Available && c360Live.status === "RUNNING" && (
                    <span style={{ marginLeft: "4px", fontSize: "10px", color: "#906806" }}>●</span>
                  )}
                </button>
              )}
            </div>
          </div>
          <div style={styles.headerRight}>
            {showPopoutButton ? (
              <button style={styles.popoutButton} onClick={handlePopOut} type="button">
                Open in new window
              </button>
            ) : null}
            {realtimeNotesEnabled && (
              <span style={getStatusStyle()}>{getStatusText()}</span>
            )}
          </div>
        </header>
      )}

      <div style={styles.mainArea}>
        <div style={styles.editorArea}>
          {visibleTab === "notes" ? (
            notes?.tree ? (
              <NotesEditor
                tree={notes.tree}
                onContentChange={handleContentChange}
                onToggleLock={handleToggleLock}
              />
            ) : (
              <div style={styles.emptyState}>
                {connectionStatus === "connecting" ? "Connecting..." : "No notes yet"}
              </div>
            )
          ) : visibleTab === "customer360" && c360Available ? (
            <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
              {/* Search bar — always visible so agents can re-query */}
              <div style={{
                padding: "8px 12px",
                borderBottom: "1px solid #ebebf0",
                background: "#ffffff",
                display: "flex",
                gap: "6px",
                alignItems: "center",
                flexShrink: 0,
              }}>
                <input
                  type="search"
                  placeholder="Phone, email, serial, or UUID"
                  defaultValue={c360Query ?? ""}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (val) { onC360Search ? onC360Search(val) : c360Hook.invoke(val); }
                    }
                  }}
                  style={{
                    flex: 1, padding: "6px 10px", fontSize: "12px",
                    border: "1px solid #d5dbdb", borderRadius: "4px", outline: "none",
                  }}
                />
                <button
                  type="button"
                  disabled={c360Live.status === "RUNNING" || c360Live.status === "INVOKING"}
                  onClick={() => {
                    const input = document.querySelector<HTMLInputElement>(
                      'input[placeholder="Phone, email, serial, or UUID"]'
                    );
                    const val = input?.value.trim();
                    if (val) { onC360Search ? onC360Search(val) : c360Hook.invoke(val); }
                  }}
                  style={{
                    padding: "6px 14px", fontSize: "12px", fontWeight: 600,
                    background: "#006ce0", color: "#fff", border: "none", borderRadius: "4px",
                    cursor: c360Live.status === "RUNNING" ? "not-allowed" : "pointer",
                    opacity: c360Live.status === "RUNNING" ? 0.6 : 1,
                  }}
                >
                  {c360Live.status === "RUNNING" || c360Live.status === "INVOKING" ? "Searching…" : "Search"}
                </button>
              </div>
              {/* Loading / progress state. Excludes SUCCEEDED so an empty
                  result (lookup completed with no matching profile) drops out
                  of the loading branch instead of spinning forever. */}
              {c360Available && !c360Profile && c360Live.status !== "FAILED" && c360Live.status !== "IDLE" && c360Live.status !== "SUCCEEDED" ? (
                <div style={{ padding: "24px 32px", flex: 1 }}>
                  <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "12px", color: "#16191f" }}>
                    Loading Customer 360…
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {Array.from(new Set(c360Live.steps.map((s) => s.name))).map((name) => {
                      const done = c360Live.steps.some((s) => s.name === name && s.event_type === "StepSucceeded");
                      const running = c360Live.steps.some((s) => s.name === name && s.event_type === "StepStarted") && !done;
                      return (
                        <div key={name} style={{ display: "flex", gap: "6px", fontSize: "12px", color: done ? "#037f0c" : running ? "#16191f" : "#9ca3af" }}>
                          <span style={{ width: "14px" }}>{done ? "✓" : running ? "⏳" : "○"}</span>
                          <span style={{ fontWeight: running ? 600 : 400 }}>{C360_STEP_LABELS[name] ?? name}</span>
                        </div>
                      );
                    })}
                    {c360Live.steps.length === 0 && (
                      <div style={{ fontSize: "11px", color: "#9ca3af" }}>Waiting for Lambda…</div>
                    )}
                  </div>
                </div>
              ) : c360Live.status === "FAILED" ? (
                <div style={{ padding: "24px 32px", color: "#d13212" }}>
                  Search failed: {String((c360Live.error as Record<string, unknown>)?.ErrorMessage ?? c360Live.error?.message ?? "Unknown error")}
                </div>
              ) : c360Profile ? (
                <>
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    {c360Variant === "tabbed" && <Customer360Tabbed data={c360Profile} />}
                    {c360Variant === "panel" && <Customer360Panel data={c360Profile} />}
                    {c360Variant === "timeline" && <Customer360Timeline data={c360Profile} />}
                  </div>
                  <div style={{
                    padding: "6px 12px",
                    borderTop: "1px solid #ebebf0",
                    background: "#f6f6f9",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                    fontSize: "11px",
                    color: "#545b64",
                  }}>
                    <span>Layout:</span>
                    {(["panel", "timeline", "tabbed"] as const).map((v) => (
                      <button
                        key={v}
                        onClick={() => handleSetC360Variant(v)}
                        style={{
                          padding: "2px 10px",
                          fontSize: "11px",
                          fontWeight: 500,
                          border: "1px solid #d5dbdb",
                          borderRadius: "4px",
                          cursor: "pointer",
                          background: c360Variant === v ? "#16191f" : "#ffffff",
                          color: c360Variant === v ? "#ffffff" : "#545b64",
                          borderColor: c360Variant === v ? "#16191f" : "#d5dbdb",
                        }}
                      >
                        {{ panel: "A: 2-Column", timeline: "B: 3-Column", tabbed: "C: Header+Tabs" }[v]}
                      </button>
                    ))}
                  </div>
                </>
              ) : c360Live.status === "SUCCEEDED" ? (
                <div style={{ padding: "24px 32px", color: "#6b7280", fontSize: "13px" }}>
                  No customer profile matched this lookup.
                </div>
              ) : null}
            </div>
          ) : (
            realtimeNotesEnabled ? <ContactSummary contactId={contactId} /> : null
          )}
        </div>
        {attachmentsEnabled ? <AttachmentsPanel contactId={contactId} /> : null}
        {/* TODO: Re-enable when ready */}
        {/* {visibleTab === "notes" && <RelatedArticles contactId={contactId} tree={notes?.tree} />} */}
        {renderDebug && renderDebug(notes)}
      </div>
    </div>
  );
}

export default NotesTaker;
